import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Gift, Copy, Users, Loader2, Share2, CheckCircle2 } from "lucide-react";

export default function AccountReferral() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/referral");
        setData(data);
      } catch (e) {
        toast.error("Errore caricamento referral");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const copy = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success("Copiato negli appunti");
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error("Impossibile copiare");
    }
  };

  const shareUrl = data?.share_url || "";

  if (loading) {
    return <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="account-referral">
      <section className="rounded-xl overflow-hidden border border-[#E8E4D9] bg-white">
        <div className="p-6 sm:p-8 bg-gradient-to-br from-blue-600 to-indigo-700 text-white relative">
          <div className="absolute -top-16 -right-16 w-64 h-64 bg-white/10 rounded-full blur-3xl pointer-events-none" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 text-xs font-medium mb-4 backdrop-blur">
              <Gift className="w-3.5 h-3.5" /> Programma Referral
            </div>
            <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight">
              Invita altri professionisti,<br />guadagna crediti fattura.
            </h2>
            <p className="mt-2 text-blue-100 text-sm max-w-md">
              Condividi il tuo codice: quando un amico si registra con il tuo codice e fa il primo acquisto,
              entrambi ricevete <strong>5 crediti bonus</strong>
            </p>
          </div>
        </div>

        <div className="p-6 grid grid-cols-1 sm:grid-cols-3 gap-4 bg-[#F5F2EA] border-t border-[#E8E4D9]">
          <Stat icon={Gift} label="Il tuo codice" value={data?.referral_code || "\u2014"} mono />
          <Stat icon={Users} label="Amici invitati" value={data?.invited_count ?? 0} />
          <Stat icon={Share2} label="Bonus per persona" value="+5 crediti" tone="emerald" />
        </div>
      </section>

      <section className="rounded-xl border border-[#E8E4D9] bg-white p-6">
        <h3 className="font-display font-semibold text-base mb-3">Il tuo link personale</h3>
        <div className="flex flex-col sm:flex-row items-stretch gap-2">
          <div className="flex-1 min-w-0 rounded-md border border-[#E8E4D9] bg-[#F5F2EA] px-3 h-11 flex items-center">
            <span className="font-mono text-sm text-zinc-800 truncate" data-testid="referral-url">{shareUrl}</span>
          </div>
          <button
            onClick={() => copy(shareUrl)}
            data-testid="copy-referral-url"
            className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-md bg-[#1D4ED8] text-white text-sm font-semibold hover:bg-[#1E3A8A] transition-colors"
          >
            {copied ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? "Copiato!" : "Copia link"}
          </button>
        </div>
        <p className="mt-3 text-xs text-zinc-500">
          Puoi condividere anche solo il codice <span className="font-mono font-semibold text-zinc-800">{data?.referral_code}</span> —
          l&apos;amico lo inserirà nel campo &quot;Codice Referral&quot; durante la registrazione.
        </p>
      </section>
    </div>
  );
}

function Stat({ icon: Icon, label, value, mono, tone = "blue" }) {
  const toneCls = tone === "emerald"
    ? "bg-emerald-50 text-emerald-700"
    : "bg-blue-50 text-blue-700";
  return (
    <div className="rounded-lg bg-white border border-[#E8E4D9] p-4">
      <div className={`inline-flex items-center justify-center w-8 h-8 rounded-md ${toneCls}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="mt-3 text-xs uppercase tracking-wider text-zinc-500 font-medium">{label}</div>
      <div className={`mt-1 font-display font-bold text-xl ${mono ? "font-mono" : ""} text-zinc-900`}>{value}</div>
    </div>
  );
}
